/**
 * CustomerQR — Displays the logged-in user's QR code for POS identification.
 *
 * QR format: fliisi://customer/{user_id}
 * Show at checkout so the cashier can scan and attach the sale to the buyer's profile.
 */
import React from 'react'
import { Platform } from 'react-native'
import QRCode from 'react-native-qrcode-svg'
import { YStack, Text, Card } from '@fliisi/pollen'
import { useLanguage } from '@fliisi/i18n'
import { useAuth } from '../contexts/AuthContext'
import { QrCode } from 'lucide-react-native'

const CUSTOMER_QR_PREFIX = 'fliisi://customer/'

/** Build QR payload from user id */
export function buildCustomerQR(userId: string): string {
  return `${CUSTOMER_QR_PREFIX}${userId}`
}

/** Parse a scanned string — returns user id if it's a customer QR, null otherwise */
export function parseCustomerQR(data: string): string | null {
  if (data.startsWith(CUSTOMER_QR_PREFIX)) {
    const id = data.slice(CUSTOMER_QR_PREFIX.length).trim()
    // Basic UUID check
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) {
      return id
    }
  }
  return null
}

interface CustomerQRProps {
  size?: number
}

export function CustomerQRDisplay({ size = 180 }: CustomerQRProps) {
  const { user, session } = useAuth()
  const { t } = useLanguage()

  if (!session || !user?.id) {
    return (
      <Card variant="flat" p="$lg" ai="center" gap="$sm">
        <QrCode size={40} color="#858B80" />
        <Text preset="bodySmall" tone="secondary" ta="center">
          {t('sign_in_for_qr')}
        </Text>
      </Card>
    )
  }

  const qrValue = buildCustomerQR(user.id)

  return (
    <Card variant="elevated" p="$lg" ai="center" gap="$md">
      <Text preset="label" ta="center">
        {t('show_at_checkout')}
      </Text>
      <YStack
        bg="white"
        p="$md"
        br="$lg"
        ai="center"
        jc="center"
      >
        {Platform.OS === 'web' ? (
          // react-native-qrcode-svg works on web via react-native-svg-web
          <QRCode
            value={qrValue}
            size={size}
            backgroundColor="white"
            color="#3E423A"
          />
        ) : (
          <QRCode
            value={qrValue}
            size={size}
            backgroundColor="white"
            color="#3E423A"
          />
        )}
      </YStack>
      <Text preset="caption" tone="secondary" ta="center">
        {t('qr_receipt_hint')}
      </Text>
    </Card>
  )
}
